import { useMemo } from 'react';

import { InfiniteData, useQueryClient } from '@mint-lib/query-client';
import { produce } from 'immer';

import * as API from '../../api/index.js';
import type {
  CellFormatType,
  TableDataFileHeader,
  TableDataFileValidatedRow,
  TableDataFileValidatedRowsResponse,
  ValidationSearchParams,
} from '../../api/types.js';
import { getPaginatedDataPlaceholder } from '../../constants/tablesConfig.js';
import { useImportHubV2 } from '../../hooks/ff/index.js';
import {
  useEnhancedMutation,
  useTypedInfiniteQuery,
  useTypedQuery,
} from '../../hooks/index.js';
import { initialSearchParams, QueryKeys } from '../queryClient.js';
import { useDataFilePreviewQueryOptions } from './preview.js';

export const useDataConfigurationErrorsCount = (
  dataFileId: string,
  options?: { enabled?: boolean; throwOnError?: boolean },
) => {
  return useTypedQuery({
    queryKey:
      QueryKeys.dataFile.steps.channel_assignment.totalErrorsCount(dataFileId),
    queryFn: () => API.getConfiguredRowsErrorsCount(dataFileId),
    ...options,
  });
};

export const useDataConfigurationRows = (
  dataFileId: string,
  searchParams: ValidationSearchParams,
  options?: { enabled?: boolean; throwOnError?: boolean },
) => {
  const isImportHubV2 = useImportHubV2();

  const endpoint = isImportHubV2
    ? API.getConfiguredDataRowsV2
    : API.getConfiguredDataRows;

  const { data: rawData, ...queryResult } = useTypedInfiniteQuery({
    queryKey: QueryKeys.dataFile.steps.channel_assignment.validatedRows(
      dataFileId,
      searchParams,
    ),
    queryFn: ({ pageParam = 1 }) => {
      return endpoint(dataFileId, {
        ...searchParams,
        pageNumber: pageParam || initialSearchParams.pageNumber,
      });
    },
    initialPageParam: initialSearchParams.pageNumber,
    getNextPageParam: (lastPage) => {
      if (lastPage.pageNumber * lastPage.itemsPerPage >= lastPage.totalItems) {
        return undefined;
      }
      return lastPage.pageNumber + 1;
    },
    placeholderData: {
      pages: [getPaginatedDataPlaceholder({ headers: [] })],
      pageParams: [],
    },
    ...options,
  });

  const headers = rawData.pages[0].additional.headers;

  const headersMeta = useMemo(() => {
    const columnIndices = headers.reduce(
      (acc, header, index) => {
        acc[header.formatType] = index;
        return acc;
      },
      {} as Record<CellFormatType, number>,
    );

    return {
      platformColumnIndex: columnIndices.platform ?? -1,
      channelColumnIndex: columnIndices.channel ?? -1,
      mediaRowColumnIndex: columnIndices.media_row ?? -1,
    };
  }, [headers]);

  const rows = useMemo(
    () => rawData.pages.flatMap((page) => page.items),
    [rawData.pages],
  );

  return {
    ...queryResult,
    data: {
      rawData,
      rows,
      headers,
      headersMeta,
    },
  };
};

export type EditDataConfigurationFieldVariables = {
  headers: TableDataFileHeader[];
  rowId: string;
  rowIndex: number;
  colIndex: number;
  value: string;
  displayValue?: string;
  platformValue: string;
  channelValue: null | string;
  mediaRowValue?: null | string;
  isBulk?: boolean;
};

type BulkEditFilter = {
  value: null | string | undefined;
  colIndex: number;
};

type BulkEditUpdate = {
  value: string;
  colIndex: number;
};

const getColumnIndicesByFormatType = (headers: TableDataFileHeader[]) =>
  headers.reduce(
    (acc, header, index) => {
      acc[header.formatType] = index;
      return acc;
    },
    {} as Record<CellFormatType, number>,
  );

const isMatchingBulkEditFilters = (
  row: TableDataFileValidatedRow,
  filters: BulkEditFilter[],
) =>
  filters.every(({ value, colIndex }) => {
    const cell = row.validatedCells[colIndex];
    const isBothInvalid = !value && cell.error;
    const isBothEqual = cell.rawCell === value;
    return isBothInvalid || isBothEqual;
  });

const applyBulkEditUpdates = (
  row: TableDataFileValidatedRow,
  colsToUpdate: BulkEditUpdate[],
) => {
  let resolvedErrorsCount = 0;

  colsToUpdate.forEach(({ value, colIndex }) => {
    const cell = row.validatedCells[colIndex];
    if (cell.error) {
      resolvedErrorsCount += 1;
    }
    if (cell.rawCell !== value) {
      cell.rawCell = value;
      cell.isEdited = true;
      cell.error = null;
    }
  });

  return resolvedErrorsCount;
};

export const useEditDataConfigurationField = (
  dataFileId: string,
  searchParams: ValidationSearchParams,
) => {
  const queryClient = useQueryClient();
  const isImportHubV2 = useImportHubV2();

  const { queryKey: previewQueryKey } = useDataFilePreviewQueryOptions({
    urlParams: { id: dataFileId },
  });
  const totalErrorsCountQueryKey =
    QueryKeys.dataFile.steps.channel_assignment.totalErrorsCount(dataFileId);
  const validatedRowsBaseQueryKey =
    QueryKeys.dataFile.steps.channel_assignment.validatedRows(dataFileId);
  const dynamicValidatedRowsQueryKey = [
    ...validatedRowsBaseQueryKey,
    searchParams,
  ];

  return useEnhancedMutation({
    mutationFn: ({
      headers,
      rowId,
      colIndex,
      value,
      platformValue,
      channelValue,
      mediaRowValue,
      isBulk,
    }: EditDataConfigurationFieldVariables) => {
      if (isBulk) {
        const headerName = headers[colIndex].headerName;
        const requiredFilters = [
          {
            headerName: 'platform',
            value: platformValue,
          },
        ];
        const optionalFilters = [
          {
            headerName: 'channel',
            value: channelValue,
          },
          {
            headerName: 'media_row',
            value: mediaRowValue,
          },
        ].filter(
          (filter) =>
            filter.value !== undefined && filter.headerName !== headerName,
        ) as Array<{
          headerName: string;
          value: null | string;
        }>;
        const filters = [...requiredFilters, ...optionalFilters];

        const bulkEditFn = isImportHubV2
          ? API.bulkEditFieldV2
          : API.bulkEditField;

        return bulkEditFn({
          dataFileId,
          payload: {
            value,
            headerName,
            filters,
          },
        });
      }

      return API.editField({
        rowId,
        payload: {
          columnPosition: headers[colIndex].columnPosition,
          rawValue: value,
        },
      });
    },
    onMutate: async ({
      headers,
      rowIndex,
      colIndex,
      value,
      platformValue,
      channelValue,
      mediaRowValue,
      isBulk,
    }: EditDataConfigurationFieldVariables) => {
      const previousPreview = queryClient.getQueryData(previewQueryKey);
      const previousTotalErrorsCount = queryClient.getQueryData<number>(
        totalErrorsCountQueryKey,
      );
      const previousValidatedRows = queryClient.getQueryData<
        InfiniteData<TableDataFileValidatedRowsResponse>
      >(dynamicValidatedRowsQueryKey);

      if (previousPreview && previousValidatedRows) {
        let resolvedErrorsCount = 0;
        const newValidatedRows = produce(previousValidatedRows, (draft) => {
          if (isBulk) {
            const {
              platform: platformColumnIndex,
              channel: channelColumnIndex,
              media_row: mediaRowColumnIndex,
            } = getColumnIndicesByFormatType(headers);

            const requiredFilters = [
              {
                value: platformValue,
                colIndex: platformColumnIndex,
              },
            ];
            const optionalFilters = [
              {
                value: channelValue,
                colIndex: channelColumnIndex,
              },
              {
                value: mediaRowValue,
                colIndex: mediaRowColumnIndex,
              },
            ].filter(({ value }) => value !== undefined);

            const activeFilters = optionalFilters.filter((filter) =>
              Boolean(filter.value),
            ) as Array<{
              value: string;
              colIndex: number;
            }>;

            const filters = [...requiredFilters, ...optionalFilters];
            const colsToUpdate = [{ colIndex, value }, ...activeFilters];

            draft.pages
              .flatMap(({ items }) => items)
              .forEach((currentRow) => {
                if (isMatchingBulkEditFilters(currentRow, filters)) {
                  resolvedErrorsCount += applyBulkEditUpdates(
                    currentRow,
                    colsToUpdate,
                  );
                }
              });
          } else {
            const pageNumber = Math.floor(
              rowIndex / Number(searchParams.itemsPerPage),
            );
            const pageRowIndex = rowIndex % Number(searchParams.itemsPerPage);
            const page = draft.pages[pageNumber];
            const row = page.items[pageRowIndex];
            const cell = row.validatedCells[colIndex];

            if (cell.error) {
              queryClient.setQueryData<number>(
                totalErrorsCountQueryKey,
                (previousValue = 0) => previousValue - 1,
              );
            }

            cell.rawCell = value;
            cell.isEdited = true;
            cell.error = null;
          }
        });
        if (resolvedErrorsCount > 0) {
          queryClient.setQueryData<number>(
            totalErrorsCountQueryKey,
            (previousValue = 0) => previousValue - resolvedErrorsCount,
          );
        }
        queryClient.setQueryData(previewQueryKey, {
          ...previousPreview,
          isEdited: true,
        });
        queryClient.setQueryData(
          dynamicValidatedRowsQueryKey,
          newValidatedRows,
        );
      }

      queryClient.removeQueries({
        queryKey: validatedRowsBaseQueryKey,
        type: 'inactive',
        exact: false,
      });

      return {
        previousPreview,
        previousTotalErrorsCount,
        previousValidatedRows,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: totalErrorsCountQueryKey,
      });
    },
    onError: (err, vars, context) => {
      if (context) {
        queryClient.setQueryData(previewQueryKey, context.previousPreview);
        queryClient.setQueryData(
          totalErrorsCountQueryKey,
          context.previousTotalErrorsCount,
        );
        queryClient.setQueryData(
          dynamicValidatedRowsQueryKey,
          context.previousValidatedRows,
        );
      }
    },
    meta: {
      getErrorsMap: (t) => ({
        default: t('Failed to edit field'),
        NotAllowedCharactersError: t(
          'Only Latin characters, numbers and special symbols are allowed',
        ),
      }),
    },
  });
};
